import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { Button } from "@altek/ui/components/ui/Button";
import { useScreenTitle } from "@/app/layouts/document-title";
import { sessionApi, type Session } from "@/features/auth/api/session.api";
import { sessionKeys } from "@/features/auth/api/session.queries";
import { AuthCardScreen } from "@/features/auth/components/AuthShell";

/**
 * Which customer am I working in?
 *
 * IT APPEARS ONLY WHEN THE ANSWER IS GENUINELY UNKNOWN — several customers within reach and
 * none chosen. Somebody who belongs to one customer never sees it, which is every person on a
 * dedicated installation and most people on the shared service; a screen with one button on it
 * is a step, not a choice.
 *
 * WHY IT BLOCKS RATHER THAN GUESSING. The server refuses to pick for a person who may reach
 * several: picking "the first" would drop an accountant into whichever client sorted first and
 * let them post to those books believing they were in another's. So the client cannot proceed
 * without an answer either — a half-loaded application behind a dismissible dialog would be the
 * same guess wearing a different hat.
 *
 * WHY IT IS NOT A DROPDOWN IN THE HEADER. The header's selector switches COMPANY inside one
 * customer, which is an everyday action. This is a different question asked once per session,
 * and putting them side by side would teach people the two are the same kind of thing.
 *
 * `onCancel` IS WHAT SEPARATES THE TWO TIMES IT APPEARS. At sign-in there is no way back — a
 * half-loaded application behind a dismissible dialog would be the same guess the server
 * refused to make. Reopened LATER from the customer strip there is: the person already has a
 * customer and may simply have wanted to check which one. Omitting the prop is what says "this
 * is the blocking one", so the blocking case cannot be turned into the dismissible one by
 * forgetting a line.
 */
export function TenantPicker({
  session,
  onCancel,
}: {
  session: Session;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();

  const choose = useMutation({
    mutationFn: (tenantId: string) => sessionApi.switchTenant(tenantId),
    onSuccess: (next) => {
      /**
       * The whole session is replaced, not invalidated.
       *
       * Permissions, reachable companies and the enabled module list all belong to the customer,
       * so an invalidate-and-refetch would leave the app holding the previous customer's menu
       * for as long as the request takes. Writing the payload the server just returned closes
       * that window entirely.
       *
       * AND THE PREVIOUS CUSTOMER'S ANSWERS GO FIRST (1 Kasım 2026). `AuthProvider` removes them when it sees the
       * identity change — in an effect, after the render that already shows the new customer's chrome over the
       * old customer's rows. Removing them before the session is written leaves nothing for that render to show.
       */
      queryClient.removeQueries({ predicate: (cached) => cached.queryKey[0] !== sessionKeys.all[0] });
      queryClient.setQueryData(sessionKeys.current(), next);
    },
  });

  /**
   * ENGELLEYEN SEÇİCİ BİR EKRAN (9 Ekim 2026, 5. tur): uygulamanın yerine çiziliyor, yani sayfanın
   * tek başlığı (`<h1>`) ve sekmenin adı bu. Kitin `CardHeader`'ı başlığı `<h2>` çiziyor; ekranda
   * hiç `<h1>` yoktu ve sekme yalnız ürün adını taşıyordu. Şeritten («Değiştir») açılan seçici
   * ise ekranın içinde bir bölüm: orada `<h2>`, sekme adı da ekranınki kalıyor.
   * SEKME ADI BUGÜN EZİLİYOR: `AppLayout` engelleyen seçiciyi `ScreenTitleContext`'in dışında
   * çiziyor ve kendi başlık etkisi (ebeveyn, sonra çalışıyor) adresin adını yazıyor — kabuğa
   * bildirildi; sağlayıcının içine alınınca bu satır kendiliğinden geçerli olur.
   */
  const blocking = onCancel === undefined;
  useScreenTitle(blocking ? "Müşteri seçin" : null);
  const Heading = blocking ? "h1" : "h2";

  const body = (
    <div className="flex flex-col gap-4">
      <div>
        <Heading className={blocking ? "text-xl font-semibold text-fg" : "text-md font-semibold text-fg"}>Müşteri seçin</Heading>
        <p className="mt-1 text-base text-fg-muted">Birden fazla müşteriye erişiminiz var. Hangisinde çalışacaksınız?</p>
      </div>
      <div className="flex flex-col gap-2">
        {session.tenant.available.map((tenant) => (
          <Button
            key={tenant.id}
            variant="secondary"
            size="touch"
            className="justify-start"
            disabled={choose.isPending}
            // Bekleme hâli BASILAN satırda: hepsi birden kapanınca hangisinin seçildiği görünmüyordu.
            loading={choose.isPending && choose.variables === tenant.id}
            onClick={() => choose.mutate(tenant.id)}
          >
            <Building2 aria-hidden className="size-4 shrink-0" />
            <span className="truncate">{tenant.displayName ?? tenant.name}</span>
          </Button>
        ))}

        {onCancel ? (
          <Button variant="ghost" className="mt-1 max-md:h-11" disabled={choose.isPending} onClick={onCancel}>
            Vazgeç
          </Button>
        ) : null}

        {choose.isError ? (
          // On the card, not in a toast: a toast disappears and leaves somebody looking at a
          // list of buttons with no idea why nothing happened. `role="alert"`: basılan düğmenin
          // sonucu, ekran okuyucu da duysun.
          <p role="alert" className="text-sm text-danger">
            Müşteri değiştirilemedi. Erişiminiz kaldırılmış olabilir; sayfayı yenileyin.
          </p>
        ) : null}
      </div>
    </div>
  );

  /** Girişte engelleyen ekran kurumun adıyla; şeritten açılan aynı soru, ekranın içinde bir kart. */
  if (blocking) return <AuthCardScreen width="max-w-md">{body}</AuthCardScreen>;
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-e1">{body}</div>
    </div>
  );
}
